import type { PlatformError, PlatformEnum } from './types';
import type { AsyncStorage } from '../core';

/**
 * 错误处理器
 * 提供统一的错误处理和降级策略
 */
export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorCallbacks: Array<(error: PlatformError) => void> = [];
  private isDebugEnabled = false;

  private constructor() {}

  /**
   * 获取单例实例
   */
  static getInstance(): ErrorHandler {
    if (!this.instance) {
      this.instance = new ErrorHandler();
    }
    return this.instance;
  }

  /**
   * 启用调试模式
   */
  enableDebug(enabled = true): void {
    this.isDebugEnabled = enabled;
  }

  /**
   * 添加错误回调
   */
  onError(callback: (error: PlatformError) => void): void {
    this.errorCallbacks.push(callback);
  }

  /**
   * 移除错误回调
   */
  offError(callback: (error: PlatformError) => void): void {
    const index = this.errorCallbacks.indexOf(callback);
    if (index > -1) {
      this.errorCallbacks.splice(index, 1);
    }
  }

  /**
   * 处理API不支持的错误
   */
  handleApiNotSupported(
    apiName: string,
    platformType: PlatformEnum,
    fallback?: () => any,
  ): any {
    const error: PlatformError = {
      type: 'api_not_supported',
      message: `API '${apiName}' 不支持在 '${platformType}' 运行`,
      apiName,
      platformType,
    };

    this.emitError(error);

    if (fallback) {
      try {
        return fallback();
      } catch (fallbackError) {
        this.handleApiFailed(apiName, platformType, fallbackError as Error);
        return this.getDefaultFallback(apiName);
      }
    }

    return this.getDefaultFallback(apiName);
  }

  /**
   * 处理API执行失败的错误
   */
  handleApiFailed(
    apiName: string,
    platformType: PlatformEnum,
    originalError: Error,
  ): any {
    const error: PlatformError = {
      type: 'api_failed',
      message: `API '${apiName}' 执行失败，'${platformType}': ${originalError.message}`,
      apiName,
      platformType,
      originalError,
    };

    this.emitError(error);
    return this.getDefaultFallback(apiName);
  }

  /**
   * 处理降级使用
   */
  handleFallbackUsed(
    apiName: string,
    platformType: PlatformEnum,
    fallbackName: string,
  ): void {
    const error: PlatformError = {
      type: 'fallback_used',
      message: `使用降级 '${fallbackName}' API '${apiName}' 在 '${platformType}'`,
      apiName,
      platformType,
    };

    this.emitError(error);
  }

  /**
   * 安全执行函数
   */
  safeExecute<T>(
    apiName: string,
    platformType: PlatformEnum,
    fn: () => T,
    fallback?: () => T,
  ): T {
    try {
      return fn();
    } catch (error) {
      return this.handleApiFailed(apiName, platformType, error as Error) as T;
    }
  }

  /**
   * 获取默认降级值
   */
  private getDefaultFallback(apiName: string): any {
    const defaults: Record<string, any> = {
      innerWidth: 375,
      innerHeight: 667,
      scrollX: 0,
      scrollY: 0,
      localStorage: this.createMockStorage(),
      navigator: this.createMockNavigator(),
      document: this.createMockDocument(),
    };

    return defaults[apiName] ?? null;
  }

  /**
   * 创建模拟的Storage对象
   */
  private createMockStorage(): AsyncStorage {
    const storage = new Map<string, string>();

    return {
      get length() {
        return storage.size;
      },
      key(index: number): string | null {
        const keys = Array.from(storage.keys());
        return keys[index] ?? null;
      },
      clear() {
        storage.clear();
      },
      getItem: async (key: string): Promise<string | null> => {
        return storage.get(key) ?? null;
      },
      removeItem(key: string): void {
        storage.delete(key);
      },
      setItem(key: string, value: string): void {
        storage.set(key, value);
      },
    };
  }

  /**
   * 创建模拟的 Navigator 对象
   */
  private createMockNavigator(): Navigator {
    return {
      userAgent: 'WPHK-Platform-Adapter/1.0.0',
      language: 'zh-CN',
      languages: ['zh-CN'],
      onLine: true,
      cookieEnabled: false,
      maxTouchPoints: 5,
      hardwareConcurrency: 4,
      deviceMemory: 4,
      platformType: 'unknown',
      vendor: 'WPHK',
      // 其他必要的属性...
    } as unknown as Navigator;
  }

  /**
   * 创建模拟的 Document 对象
   */
  private createMockDocument(): Document {
    return {
      documentElement: {} as HTMLElement,
      body: {} as HTMLBodyElement,
      head: {} as HTMLHeadElement,
      title: 'Mock Document',
      referrer: '',
      domain: '',
      URL: '',
      // 其他必要的属性...
    } as unknown as Document;
  }

  /**
   * 发出错误事件
   */
  private emitError(error: PlatformError): void {
    if (this.isDebugEnabled) {
      console.warn(`[PlatformAdapter] ${error.message}`, error);
    }

    this.errorCallbacks.forEach((callback) => {
      try {
        callback(error);
      } catch (callbackError) {
        console.error('Error callback failed:', callbackError);
      }
    });
  }
}
